import ErrorPage from "@/pages/_error";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import UserMarkdown from "@/components/UserMarkdown";
import { EntityBadgeList } from "@/components/EntityBadgeList";
import { useSearchParams } from "react-router";
import { Badge } from "@/assets/badges/__generated__/badges.graphql";
import { UserActivity } from "@/components/UserActivity";
import { graphql, Environment } from "react-relay";
import { LoaderArgs } from "@/utils/route";
import { EntityOverviewPageQuery as EntityOverviewPageQueryType } from "./__generated__/EntityOverviewPageQuery.graphql";
import { preload, usePreloaded } from "@/utils/relay";
import { getActivityFiltersParams } from "@/utils/activityTracker";
export default function EntityPage() {
  const [searchParams, _] = useSearchParams();
  const intl = useIntl();
  const { variables, query } = useEntityOverviewPageQuery();
  if (!query.entityByUsername) {
    return (
      <ErrorPage
        status={404}
        message={intl.formatMessage(
          { defaultMessage: "@{username} not found" },
          { username: variables.username },
        )}
      />
    );
  }
  const entity = query.entityByUsername;
  const sharedBadge = searchParams.get("badge") as Badge;
  return (
    <div className="flex flex-col gap-4">
      {entity.viewerCanReadActivity && (
        <UserActivity
          user={entity}
          date={variables.activity_date}
          kind={variables.activity_kinds?.at(0)}
        />
      )}
      <div className="text-gray-500">
        {entity.bio ? (
          <UserMarkdown>{entity.bio}</UserMarkdown>
        ) : (
          <FormattedMessage defaultMessage="No description" />
        )}
      </div>
      <EntityBadgeList entity={entity} sharedBadge={sharedBadge} />
    </div>
  );
}
const EntityOverviewPageQuery = graphql`
  query EntityOverviewPageQuery(
    $username: String!
    $activity_kinds: [EntityActivitiesConnectionKind!]
    $activity_date: String!
  ) {
    entityByUsername(username: $username) {
      id
      bio
      ...EntityBadgeListFragment
      ...EntityHelmetFragment
      ... on User {
        viewerCanReadActivity: viewerCan(action: READ_ACTIVITY_TRACKER)
        ...UserActivityFragment
          @arguments(kinds: $activity_kinds, date: $activity_date)
      }
    }
  }
`;
export const loader =
  (environment: Environment) =>
  ({ params: { username }, request }: LoaderArgs) => {
    if (!username) {
      throw new Response("No username", { status: 500 });
    }
    const { year, kind } = getActivityFiltersParams(request) || {};
    return preload<EntityOverviewPageQueryType>(
      environment,
      EntityOverviewPageQuery,
      {
        username,
        activity_kinds: kind ? [kind] : undefined,
        activity_date: year
          ? `${year}-01-01`
          : `${new Date().getFullYear().toString()}-01-01`,
      },
    );
  };
const useEntityOverviewPageQuery = () =>
  usePreloaded<EntityOverviewPageQueryType>();

export const lazyHandle = {
  titleI18n: defineMessage({ defaultMessage: "Overview" }),
};
